import { TFunction } from 'i18next'

import { TLoadableSettled } from 'src/utils/Loadable'
import {
  TGroupReference,
  TUseAlarmAssignedEntitiesResult,
  isGroup,
  isPassport,
} from 'src/parser/entities/Alarm/Preview/Provider/useAlarmAssignedEntity'

export const ASSIGNED_I18N_KEY = 'alarm_report.header.assigned'

const LIST_SEPARATOR = ', '

export type TAssignedField = {
  context: string
  i18nKey: string
  values: Record<string, unknown>
}

export const getAssignedFields = ({
  assignedEntities,
  t,
}: {
  assignedEntities: TLoadableSettled<TUseAlarmAssignedEntitiesResult>
  t: TFunction
}): TAssignedField[] => {
  if (assignedEntities.error !== null)
    return [field('error', { context: 'error' })]

  const passports = assignedEntities.data.filter(isPassport)
  const groups = assignedEntities.data.filter(isGroup)

  if (passports.length === 0 && groups.length === 0)
    return [field('none', { context: 'none' })]

  const fields: TAssignedField[] = []

  if (passports.length === 1)
    fields.push(
      field('passport', { context: 'passport', passport: passports[0] }),
    )
  else if (passports.length > 1)
    fields.push(
      field('passports', {
        context: 'passports',
        list: passports.map(it => it.username).join(LIST_SEPARATOR),
      }),
    )

  if (groups.length === 1)
    fields.push(
      field('group', {
        context: 'group',
        group: {
          ...groups[0],
          '@nameLeaf': translateGroupLabel({ group: groups[0], t }),
        },
      }),
    )
  else if (groups.length > 1)
    fields.push(
      field('groups', {
        context: 'groups',
        list: groups.map(getGroupLabel).join(LIST_SEPARATOR),
      }),
    )

  return fields
}

export const getGroupLabel = (group: TGroupReference): string =>
  group.name === undefined ? (group.id ?? '') : getGroupNameLeaf(group.name)

export const getGroupNameLeaf = (name: string): string => {
  if (name === '/') return name

  return name.replace(/\/+$/, '').split('/').at(-1) ?? ''
}

const translateGroupLabel = ({
  group,
  t,
}: {
  group: TGroupReference
  t: TFunction
}): string => {
  const label = getGroupLabel(group)

  return t('entities.group.@nameLeaf', {
    context: label.trim() === '' ? 'notPrintable' : undefined,
    group: {
      ...group,
      '@nameLeaf': label,
    },
  })
}

const field = (
  context: string,
  values: Record<string, unknown>,
): TAssignedField => ({
  context,
  i18nKey: ASSIGNED_I18N_KEY,
  values,
})
